/**
 * Trusted Types `default` policy.
 *
 * The CSP shipped by HRxPortalCDK includes `require-trusted-types-for 'script'` in
 * report-only mode. Every DOM sink that takes a string (script src, innerHTML, eval, ...)
 * therefore reports a violation. The report-only header cannot tell us what to fix:
 *   - the CSP spec truncates `script_sample` to ~70 characters, so URLs are cut mid-host,
 *   - reports carry no stack, only a position inside a minified bundle,
 *   - Sentry groups every violation into a single issue by design.
 *
 * This policy is the replacement instrument. It is deliberately PASS-THROUGH: every method
 * returns its input unchanged, so nothing is ever blocked and no behaviour changes. What it
 * adds is a record of each distinct sink usage with the *untruncated* argument, which is the
 * signal we need to build the eventual allowlists.
 *
 * A `default` policy (rather than a named one) is required because the two script loaders in
 * this app cannot be pointed at a named policy:
 *   - webpack 5.25.1 predates `output.trustedTypes` (added in 5.37),
 *   - `module-federation-import-remote` hand-ports webpack's loader and never reads it.
 * The browser falls back to the `default` policy for both.
 *
 * Registered as the first import of `src/index.tsx` so it exists before the first dynamic
 * import triggers webpack's script-tag chunk loader.
 */

/** Sinks the browser routes through a Trusted Types policy. */
export type TrustedTypeSink = 'createHTML' | 'createScript' | 'createScriptURL';

export interface TrustedTypeSinkReport {
  sink: TrustedTypeSink;
  /** Stable identity for this usage — an origin for URLs, a content hash otherwise. */
  key: string;
  /**
   * Leading excerpt of the value. Script URLs additionally have their query string and
   * fragment stripped, since those can carry credentials and are not needed for aggregation.
   */
  sample: string;
  /** Full length of the original argument, so truncation of `sample` is visible. */
  sampleLength: number;
  /** Call stack at the sink. Frames are minified — sourcemaps are not uploaded to Sentry. */
  stack?: string;
  count: number;
}

/**
 * Distinct-key ceiling. Sinks are driven by user navigation, so the key space is naturally
 * small (measured: ~20 distinct values across 1.4K violations). The cap only guards against
 * an unforeseen high-cardinality sink exhausting memory.
 */
const MAX_TRACKED_KEYS = 200;

/** Excerpt length for HTML/script bodies. Long enough to identify, short enough to send. */
const SAMPLE_LENGTH = 200;

const reports = new Map<string, TrustedTypeSinkReport>();

/** Usages dropped because MAX_TRACKED_KEYS was reached, keyed by sink. */
const overflowCounts = new Map<TrustedTypeSink, number>();

/**
 * Collapses a value to a stable identity so repeated usages aggregate instead of flooding.
 *
 * Script URLs reduce to their origin: what we ultimately need is the set of origins to
 * allowlist, and per-file paths (plus the cache-busting `?t=` query that
 * `setRemoteEntryFileUrlWindowAttributes` appends) would otherwise produce a new key per
 * load. Everything else is hashed, since HTML and script bodies have no meaningful prefix.
 *
 * Opaque-origin schemes are excluded from the origin path deliberately. `blob:`, `data:` and
 * `filesystem:` all parse successfully but report an origin of `"null"`, so every distinct
 * value would collapse into a single meaningless key — and the application does create blob
 * workers (see `src/utils/DiceWorkerFactory.ts`). Hashing keeps those distinguishable.
 */
const OPAQUE_ORIGIN_SCHEMES = /^(blob|data|filesystem):/i;

const buildKey = (sink: TrustedTypeSink, value: string): string => {
  if (sink === 'createScriptURL' && !OPAQUE_ORIGIN_SCHEMES.test(value)) {
    try {
      const { origin } = new URL(value, window.location.href);
      // A parseable URL can still yield an opaque origin; fall through rather than key on it.
      if (origin && origin !== 'null') {
        return `${sink}|${origin}`;
      }
    } catch {
      // Genuinely unparseable (e.g. a bare `http://`) — fall through to hashing.
    }
  }
  // FNV-1a. Not cryptographic; only needs to be stable and cheap.
  let hash = 0x811c9dc5;
  const scoped = value.slice(0, SAMPLE_LENGTH);
  for (let i = 0; i < scoped.length; i++) {
    hash ^= scoped.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${sink}|${(hash >>> 0).toString(16)}`;
};

/**
 * Reduces a script URL to what is safe to report.
 *
 * The query string and fragment are dropped. Aggregation keys on origin alone, so neither
 * carries diagnostic value, and script URLs can legitimately carry signed tokens or session
 * credentials — this policy intercepts every script load in the document, including remotes
 * owned by other teams, so the parameters cannot be assumed benign.
 *
 * The result is still capped: `data:` script URLs embed the entire script body in the URL, so
 * they are unbounded and content-bearing even after the query is stripped.
 */
const redactScriptUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.href);
    const redacted = url.search || url.hash ? `${url.origin}${url.pathname}` : value;
    return redacted.slice(0, SAMPLE_LENGTH);
  } catch {
    // Unparseable, so there is no query to isolate — cap it and move on.
    return value.slice(0, SAMPLE_LENGTH);
  }
};

/**
 * Records one sink usage. Never throws: a fault in instrumentation must not break the page,
 * because the policy sits in front of every script load in the application.
 */
const record = (sink: TrustedTypeSink, value: string): void => {
  try {
    const key = buildKey(sink, value);
    const existing = reports.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }
    if (reports.size >= MAX_TRACKED_KEYS) {
      overflowCounts.set(sink, (overflowCounts.get(sink) ?? 0) + 1);
      return;
    }
    reports.set(key, {
      sink,
      key,
      sample: sink === 'createScriptURL' ? redactScriptUrl(value) : value.slice(0, SAMPLE_LENGTH),
      sampleLength: value.length,
      stack: new Error().stack,
      count: 1,
    });
  } catch {
    // Instrumentation failure is not worth breaking a script load over.
  }
};

/** Snapshot of everything recorded so far. */
export const getTrustedTypeSinkReports = (): TrustedTypeSinkReport[] => Array.from(reports.values());

/** Usages dropped after hitting MAX_TRACKED_KEYS, keyed by sink. */
export const getTrustedTypeSinkOverflow = (): Record<string, number> => Object.fromEntries(overflowCounts);

/** Test seam. */
export const resetTrustedTypeSinkReports = (): void => {
  reports.clear();
  overflowCounts.clear();
};

/**
 * Registers the policy. Idempotent and non-throwing.
 *
 * @returns true if this call created the policy.
 */
export const registerTrustedTypesPolicy = (): boolean => {
  // Absent in Firefox and Safari, which simply never enforce Trusted Types.
  if (typeof window === 'undefined' || !window.trustedTypes?.createPolicy) {
    return false;
  }
  // Another bundle (a micro frontend, or a second copy of this module) may have registered
  // first. Re-registering `default` throws, so bail out rather than risk it.
  if (window.trustedTypes.defaultPolicy) {
    return false;
  }
  try {
    window.trustedTypes.createPolicy('default', {
      createHTML: (input: string) => {
        record('createHTML', input);
        return input;
      },
      // The Trusted Types spec requires createScript to return a string identical to its
      // input, or the browser throws EvalError. This sink can only ever observe.
      createScript: (input: string) => {
        record('createScript', input);
        return input;
      },
      createScriptURL: (input: string) => {
        record('createScriptURL', input);
        return input;
      },
    });
    return true;
  } catch {
    // Name collision lost a race, or the factory rejected the policy. Either way the page
    // must still boot; Trusted Types stays in report-only mode until we ship enforcement.
    return false;
  }
};

registerTrustedTypesPolicy();
